import {
  type CellContext,
  createColumnHelper,
  type PaginationState,
} from '@tanstack/react-table';
import { Loader2Icon, PencilIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import {
  notifyTagSaved,
  type Tag,
  useCreateTagMutation,
  useGetTags,
  useUpdateTagMutation,
} from '~entities/tags';
import { useDeleteTagMutation } from '~entities/tags/api/delete-tag';

import { EnrollioTable } from '~shared/components/enrollio-table';
import { DataTableColumnHeader } from '~shared/components/table';
import { getStoredPageSize } from '~shared/helpers/page-size-preference';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~shared/shadcn/ui/alert-dialog';
import { Button, buttonVariants } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~shared/shadcn/ui/dialog';
import { Input } from '~shared/shadcn/ui/input';
import { Label } from '~shared/shadcn/ui/label';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '~shared/shadcn/ui/tooltip';

const columnHelper = createColumnHelper<Tag>();

const columns = [
  columnHelper.accessor('fieldName', {
    header: (info) => (
      <DataTableColumnHeader
        disableHiding
        column={info.column}
        title='Tag Name'
        className='pl-4 text-black'
      />
    ),
    cell: (info) => <div className='px-4 capitalize'>{info.getValue()}</div>,
  }),

  columnHelper.display({
    id: 'Action',
    header: (info) => (
      <DataTableColumnHeader
        disableHiding
        column={info.column}
        title='Action'
        className='justify-end px-4 text-black'
      />
    ),
    cell: (info) => {
      return <TagActionCell info={info} />;
    },
  }),
];

export function TagListPage() {
  const [query, setQuery] = useState<string>('');

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: getStoredPageSize(),
  });

  const { data, isFetching } = useGetTags({
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    ...(query ? { search: query } : {}),
  });

  const tableData = data?.items ?? [];

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const createMutation = useCreateTagMutation();

  const handleCreateTag = async () => {
    try {
      const created = await createMutation.mutateAsync({
        fieldName: newTagName,
      });
      setIsAddDialogOpen(false);
      setNewTagName('');
      notifyTagSaved('Tag created successfully', created);
    } catch (error) {
      toast.error('Failed to create tag');
    }
  };

  return (
    <div className='w-full min-w-0 md:w-1/2 lg:w-1/3'>
      <EnrollioTable
        ariaLabel='Tags'
        totalLabel='Tags'
        filterHeader={
          <Input
            placeholder='Search Tags'
            value={query}
            className='w-full sm:w-64'
            onChange={(event) => {
              setQuery(event.target.value);
            }}
          />
        }
        toolbar={
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button
                variant='secondary'
                size='sm'
                className='w-full sm:w-auto'
              >
                <PlusIcon />
                Add New
              </Button>
            </DialogTrigger>
            <DialogContent className='sm:max-w-[425px]'>
              <DialogHeader>
                <DialogTitle>Add New Tag</DialogTitle>
                <DialogDescription>
                  Create a new tag by entering the name below.
                </DialogDescription>
              </DialogHeader>
              <div className='grid gap-4 py-4'>
                <div className='grid grid-cols-4 items-center gap-4'>
                  <Label htmlFor='name' className='text-right'>
                    Tag Name
                  </Label>
                  <Input
                    id='name'
                    value={newTagName}
                    onChange={(event) => {
                      setNewTagName(event.target.value);
                    }}
                    className='col-span-3'
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant={'outline'}
                  onClick={() => {
                    setIsAddDialogOpen(false);
                    setNewTagName('');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type='submit'
                  onClick={handleCreateTag}
                  disabled={createMutation.isPending || !newTagName.trim()}
                >
                  {createMutation.isPending ? (
                    <Loader2Icon className='animate-spin' />
                  ) : (
                    'Create'
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
        data={tableData}
        columns={columns}
        rowCount={data?.pagination.total}
        isUpdating={isFetching}
        manualPagination
        pagination={pagination}
        onPaginationChange={setPagination}
      />
    </div>
  );
}

function TagActionCell({ info }: { info: CellContext<Tag, unknown> }) {
  const mutation = useUpdateTagMutation();

  const initialTagName = info.row.original.fieldName;
  const tagId = info.row.original._id;

  const [tagName, setTagName] = useState(initialTagName);
  const [isOpen, setIsOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const deleteMutation = useDeleteTagMutation();

  const handleDeleteTag = async (event: React.MouseEvent) => {
    event.preventDefault();
    try {
      await deleteMutation.mutateAsync({
        fieldId: tagId,
      });
      toast.success('Tag deleted successfully');
      setIsDeleteDialogOpen(false);
    } catch (error) {
      toast.error('Failed to delete tag');
    }
  };

  const handleSave = async () => {
    try {
      const updated = await mutation.mutateAsync({
        fieldId: tagId,
        fieldName: tagName,
      });
      setIsOpen(false);
      notifyTagSaved('Tag updated successfully', updated);
    } catch (error) {
      console.error(error);
      toast.error('Failed to update tag');
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <div className='flex items-center justify-end gap-2 px-4'>
          <TooltipProvider>
            <Tooltip>
              <DialogTrigger asChild>
                <TooltipTrigger asChild>
                  <Button className='size-6 p-0 text-secondary [&_svg]:size-3'>
                    <PencilIcon />
                  </Button>
                </TooltipTrigger>
              </DialogTrigger>
              <TooltipContent>
                <p className='text-secondary'>Edit Tag</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={'destructive'}
                  className='size-6 p-0 [&_svg]:size-3'
                  onClick={() => {
                    setIsDeleteDialogOpen(true);
                  }}
                >
                  <Trash2Icon />
                </Button>
              </TooltipTrigger>
              <TooltipContent className='bg-destructive'>
                <p className='text-destructive-foreground'>Delete Tag</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <DialogContent className='sm:max-w-[425px]'>
          <DialogHeader>
            <DialogTitle>Edit Tag</DialogTitle>
            <DialogDescription>
              Make changes to your Tag here. Click save when you&apos;re done.
            </DialogDescription>
          </DialogHeader>
          <div className='grid gap-4 py-4'>
            <div className='grid grid-cols-4 items-center gap-4'>
              <Label htmlFor='name' className='text-right'>
                Tag Name
              </Label>
              <Input
                id='name'
                value={tagName}
                onChange={(event) => {
                  setTagName(event.target.value);
                }}
                className='col-span-3'
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant={'outline'}
              onClick={() => {
                setIsOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              type='submit'
              onClick={handleSave}
              disabled={mutation.isPending}
            >
              {mutation.isPending ? (
                <Loader2Icon className='animate-spin' />
              ) : (
                'Save'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete tag &ldquo;{initialTagName}&rdquo;?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the tag definition and automatically
              un-tag every contact that currently has it. This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className={buttonVariants({ variant: 'destructive' })}
              onClick={handleDeleteTag}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? (
                <Loader2Icon className='animate-spin' />
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
